import {
	Component,
	NgZone,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CustomModalService } from './services/modal/custom-modal.service';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { NotificationDialogComponent } from '@lmc-app/shared/components/notification-dialog/notification-dialog.component';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { UserService } from '@lmc-app/core/services/user.service';
import { NotificationService } from '@lmc-app/core/services/notification.service';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { ApplicationCallStatus } from '@lmc-app/shared/enum/application-call-status.enum';
import { ApplicationCallRequest } from '@lmc-app/shared/interface/request/application-call.request';
import { ScheduleCallFormComponent } from '@lmc-app/summary/activities/call/schedule-call-form.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import {
	ScheduledCallReminderDialogComponent,
	ScheduledCallReminderDialogData,
} from '@lmc-app/shared/components/scheduled-call-reminder-dialog/scheduled-call-reminder-dialog.component';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';
import { User } from '@lmc-app/shared/interface/user.interface';
import { AuthStatus } from '@lmc-app/shared/enum/auth-status.enum';
import { Store } from '@ngrx/store';
import { selectAuthStatus, State } from '@lmc-app/store';
import { VersionCheckService } from '@lmc-app/core/services/version-check.service';
import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { TopNavComponent } from './top-nav/top-nav.component';
import { NavVisibilityDirective } from './shared/directives/nav-visibility.directive';
import { CustomModalComponent } from './custom-modal/custom-modal.component';
import { NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BackgroundTaskIndicatorComponent } from './shared/components/background-task-indicator/background-task-indicator.component';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./app.component.scss'],
	imports: [
		TopNavComponent,
		NavVisibilityDirective,
		CustomModalComponent,
		NgIf,
		MatProgressSpinner,
		RouterOutlet,
		BackgroundTaskIndicatorComponent,
	],
})
export class AppComponent implements OnInit, OnDestroy {
	modalOptions: any;
	modalActiveSubscription: Subscription;
	currentUser: User | null = null;
	authStatus: AuthStatus = AuthStatus.UNKNOWN;
	private readonly _onDestroy = new Subject<void>();
	private readonly notificationChannel = new BroadcastChannel(
		'firebase-notifications'
	);
	private readonly processedMessageIds = new Set<string>();

	constructor(
		private readonly afMessaging: AngularFireMessaging,
		private readonly applicationService: ApplicationService,
		private readonly userService: UserService,
		private readonly notificationService: NotificationService,
		private readonly customModalService: CustomModalService,
		private readonly dialog: MatDialog,
		private readonly bottomSheet: MatBottomSheet,
		private readonly snackBar: MatSnackBar,
		private readonly router: Router,
		private readonly store: Store<State>,
		private readonly ngZone: NgZone,
		private readonly versionCheckService: VersionCheckService
	) {
		pdfDefaultOptions.assetsFolder = 'assets/pdfViewer';
	}

	get showLoadingOverlay(): boolean {
		// Only show overlay when actively checking auth (LOADING)
		// UNKNOWN means we haven't checked yet (public routes) - don't block UI
		// LOADING means auth check in progress (guard triggered) - show overlay
		return this.authStatus === AuthStatus.LOADING;
	}

	ngOnInit() {
		this.versionCheckService.init();

		this.modalActiveSubscription =
			this.customModalService.modalActive.subscribe((modalOptions) => {
				this.modalOptions = modalOptions;
			});

		// Subscribe to auth status to control loading overlay
		this.store
			.select(selectAuthStatus)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((status) => {
				this.authStatus = status;
			});

		// Subscribe to user changes
		this.userService.user$
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user) => {
				this.currentUser = user;

				// Request notification permission when user logs in and has permission
				if (this.currentUser?.allowedNotification) {
					this.afMessaging.requestPermission.subscribe({
						next: () => {
							this.subscribeToTokenChanges();
						},
						error: (error) => {
							console.error('Notification permission denied:', error);
						},
					});
				}
			});

		// Listen for foreground messages:
		// 1. Handle directly (sender does NOT receive its own BroadcastChannel message)
		// 2. Broadcast to all OTHER tabs
		this.afMessaging.messages.subscribe((message) => {
			this.handleForegroundMessage(message);
			this.notificationChannel.postMessage(message);
		});

		// Receive messages from other tabs (foreground broadcast) or Service Worker (background)
		// NgZone.run() is required because BroadcastChannel.onmessage fires outside Angular Zone,
		// which prevents Change Detection from running and causes the dialog to render without data
		this.notificationChannel.onmessage = (event) => {
			this.ngZone.run(() => {
				this.handleForegroundMessage(event.data);
			});
		};
	}

	ngOnDestroy() {
		this.modalActiveSubscription.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
		this.notificationChannel.close();
	}

	openCustomerAlertDialog(customerData: ScheduledCallReminderDialogData) {
		const dialogRef = this.dialog.open(ScheduledCallReminderDialogComponent, {
			width: '500px',
			data: customerData,
			disableClose: false,
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (result && result.action !== 'close') {
				// Handle the different actions
				switch (result.action) {
					case 'completed':
						await this.updateApplicationCallStatus(
							result.applicationId,
							result.applicationCallId,
							ApplicationCallStatus.COMPLETED
						);
						break;
					case 'reschedule':
						await this.openRescheduleDialogWithData(
							result.application,
							result.applicationCall
						);
						break;
					case 'cancel':
						await this.updateApplicationCallStatus(
							result.applicationId,
							result.applicationCallId,
							ApplicationCallStatus.CANCELLED
						);
						break;
				}
			}
		});
	}

	async handleForegroundMessage(message: any) {
		// Deduplicate messages by messageId to prevent double processing.
		// Firebase delivers the same message to all open tabs, and each tab also
		// broadcasts via BroadcastChannel, causing every tab to receive and process
		// the same message twice.
		const messageId = message.messageId;
		if (messageId) {
			if (this.processedMessageIds.has(messageId)) {
				return;
			}
			this.processedMessageIds.add(messageId);
			// Clean up after 30 seconds to prevent memory leak
			setTimeout(() => this.processedMessageIds.delete(messageId), 30000);
		}

		// Close any existing notification dialogs
		this.dialog.openDialogs.forEach((dialog) => {
			if (dialog.componentInstance instanceof NotificationDialogComponent) {
				dialog.close({ action: 'dismissed-by-new' });
			}
		});

		// Check if message has data and notificationType
		if (!message.data || !message.data.notificationType) {
			console.log('Received notification without notificationType:', message);
			return;
		}

		const notificationType = message.data.notificationType;
		try {
			switch (notificationType) {
				case 'SCHEDULED_CALL_REMINDER': {
					const { applicationId, applicationCallId } = message.data;
					const application = await this.getApplication(applicationId);
					const applicationCall = await this.getApplicationCall(
						applicationId,
						applicationCallId
					);

					const customerData: ScheduledCallReminderDialogData = {
						applicationId: applicationId,
						applicationCallId: applicationCallId,
						dateTime: applicationCall.dateTime,
						customerName:
							applicationCall.applicant === Applicant.MAIN
								? application.Deal_Name
								: application.Co_Applicant_Name,
						bestContactNumber:
							applicationCall.applicant === Applicant.MAIN
								? application.Mobile
								: application.Secondary_Cell_Number,
						otherContactNumber:
							applicationCall.applicant === Applicant.MAIN
								? application.Phone
								: application.Secondary_Home_Number,
						email:
							applicationCall.applicant === Applicant.MAIN
								? application.Email1
								: application.Email,
						application: application,
						applicationCall: applicationCall,
					};
					this.openCustomerAlertDialog(customerData);
					break;
				}
				case 'TEST': {
					const notification = new Notification(
						message.notification?.title || 'Test Notification',
						{
							body: message.notification?.body || 'Test message from LMCredit',
							icon: '/favicon.ico',
							tag: 'test-notification',
							requireInteraction: false,
						}
					);

					// Auto close after 5 seconds
					setTimeout(() => notification.close(), 5000);

					// Log when notification is clicked
					notification.onclick = () => {
						console.log('Test notification clicked');
						notification.close();
						window.focus();
					};
					break;
				}
				default: {
					console.log('Unknown notification type:', notificationType);
					break;
				}
			}
		} catch (error) {
			console.error('Error handling foreground message:', error);
		}
	}

	getApplication = (applicationId: string) => {
		return new Promise<any>(async (resolve, reject) => {
			this.applicationService.getApplication(applicationId).subscribe({
				next: (res) => {
					resolve(res);
				},
				error: (error) => {
					console.error(error);
					reject(error);
				},
			});
		});
	};

	getApplicationCall = (applicationId: string, applicationCallId: string) => {
		return new Promise<ApplicationCall>(async (resolve, reject) => {
			this.applicationService
				.getApplicationCall(applicationId, applicationCallId)
				.subscribe({
					next: (res) => {
						resolve(res);
					},
					error: (error) => {
						console.error(error);
						reject(error);
					},
				});
		});
	};

	async updateApplicationCallStatus(
		applicationId: string,
		applicationCallId: string,
		status: ApplicationCallStatus
	): Promise<void> {
		try {
			// Get current user employeeID from userService
			const employeeID = this.currentUser?.employeeID || 0;

			const request: ApplicationCallRequest = {
				status: status,
				userId: employeeID,
			};

			await new Promise<void>((resolve, reject) => {
				this.applicationService
					.updateApplicationCall(applicationId, applicationCallId, request)
					.subscribe({
						next: () => {
							const statusText =
								status === ApplicationCallStatus.COMPLETED
									? 'completed'
									: 'cancelled';
							this.snackBar.open(
								`Call ${statusText} successfully`,
								SnackbarConstants.ACTION_DISMISS,
								SnackbarConstants.DURATION_DISMISS_SHORT
							);
							resolve();
						},
						error: (error) => {
							console.error(error);
							this.snackBar.open(
								error.error?.message || `Failed to update call status`,
								SnackbarConstants.ACTION_DISMISS,
								SnackbarConstants.DURATION_DISMISS_SHORT
							);
							reject(error);
						},
					});
			});
		} catch (error) {
			console.error('Error updating application call status:', error);
		}
	}

	async openRescheduleDialogWithData(
		application: any,
		applicationCall: ApplicationCall
	): Promise<void> {
		try {
			this.bottomSheet
				.open(ScheduleCallFormComponent, {
					panelClass: 'bottom-sheet-width',
					data: {
						application: application,
						applicationCall: applicationCall,
						currentUser: this.currentUser,
					},
				})
				.afterDismissed()
				.subscribe((result) => {
					if (result) {
						console.log('Schedule call form completed successfully');
						this.snackBar.open(
							'Call rescheduled successfully',
							SnackbarConstants.ACTION_DISMISS,
							SnackbarConstants.DURATION_DISMISS_SHORT
						);
					}
				});
		} catch (error) {
			console.error('Error opening reschedule dialog:', error);
			this.snackBar.open(
				'Failed to open reschedule dialog',
				SnackbarConstants.ACTION_DISMISS,
				SnackbarConstants.DURATION_DISMISS_SHORT
			);
		}
	}

	/**
	 * Subscribe to Firebase token changes
	 * Called after notification permission is granted
	 * Automatically pushes token to backend when user is logged in and has permission
	 */
	private subscribeToTokenChanges(): void {
		this.afMessaging.tokenChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((token) => {
				if (token && this.currentUser?.allowedNotification) {
					this.notificationService.registerFirebaseToken(token).subscribe({
						next: () => {
							console.log('Token successfully registered in backend');
						},
						error: (error) => {
							console.error('Failed to register token in backend:', error);
						},
					});
				}
			});
	}
}
